import { makeAutoObservable, runInAction } from "mobx";
import { superadminGetServiceCenterBookings } from "@pro-sto/api-client";
import type {
  BookingItemDto as BookingItem,
  PaginationMetaDto as PaginationMeta,
  SuperadminGetServiceCenterBookingsData,
} from "@pro-sto/api-client";
import { toastStore } from "../ToastStore";

const DEFAULT_META: PaginationMeta = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
};

export class BookingsSubStore {
  items: BookingItem[] = [];
  meta: PaginationMeta = { ...DEFAULT_META };
  isLoading = false;
  error = false;

  /** Monotonic counter to discard stale responses */
  private reqSeq = 0;

  search = "";
  statusFilter: string[] = [];
  paymentFilter: string[] = [];
  sort = "-start_time";
  dateFrom = "";
  dateTo = "";

  constructor(private getUuid: () => string) {
    makeAutoObservable<this, 'reqSeq'>(this, { reqSeq: false });
  }

  setSearch = (q: string) => {
    this.search = q;
  };
  setStatusFilter = (val: string[]) => {
    this.statusFilter = val;
  };
  setPaymentFilter = (val: string[]) => {
    this.paymentFilter = val;
  };
  setSort = (sort: string) => {
    this.sort = sort;
  };
  setDateRange = (from: string, to: string) => {
    this.dateFrom = from;
    this.dateTo = to;
  };

  private buildParams(
    page: number,
  ): Omit<SuperadminGetServiceCenterBookingsData, "uuid"> {
    return {
      page,
      limit: this.meta.limit,
      ...(this.search && { q: this.search }),
      ...(this.sort && { sort: this.sort }),
      ...(this.statusFilter.length > 0 && {
        filterStatusEq: this
          .statusFilter[0] as SuperadminGetServiceCenterBookingsData["filterStatusEq"],
      }),
      ...(this.paymentFilter.length > 0 && {
        filterPaymentStatusEq: this
          .paymentFilter[0] as SuperadminGetServiceCenterBookingsData["filterPaymentStatusEq"],
      }),
      ...(this.dateFrom && { filterDateFromEq: this.dateFrom }),
      ...(this.dateTo && { filterDateToEq: this.dateTo }),
    };
  }

  load = async (page = 1) => {
    const uuid = this.getUuid();
    if (!uuid) return;
    const reqId = ++this.reqSeq;
    runInAction(() => {
      this.isLoading = true;
      this.error = false;
    });
    try {
      const res = await superadminGetServiceCenterBookings({
        uuid,
        ...this.buildParams(page),
      });
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.items = res.data ?? [];
        this.meta = res.meta;
        this.isLoading = false;
        this.error = false;
      });
    } catch {
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.isLoading = false;
        this.error = true;
      });
      toastStore.showError("Не удалось загрузить бронирования");
    }
  };

  loadMore = async () => {
    if (!this.meta.hasNextPage || this.isLoading) return;
    const uuid = this.getUuid();
    if (!uuid) return;
    const reqId = ++this.reqSeq;
    this.isLoading = true;
    try {
      const res = await superadminGetServiceCenterBookings({
        uuid,
        ...this.buildParams(this.meta.page + 1),
      });
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        const existingUuids = new Set(this.items.map((i) => i.uuid));
        const newItems = (res.data ?? []).filter(
          (i) => !existingUuids.has(i.uuid),
        );
        this.items = [...this.items, ...newItems];
        this.meta = res.meta;
        this.isLoading = false;
      });
    } catch {
      if (reqId !== this.reqSeq) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить бронирования");
    }
  };

  reset = () => {
    this.items = [];
    this.meta = { ...DEFAULT_META };
    this.search = "";
    this.statusFilter = [];
    this.paymentFilter = [];
    this.sort = "-start_time";
    this.dateFrom = "";
    this.dateTo = "";
    this.error = false;
  };
}
